import React from 'react';
import {Modal} from '@mc/ui-v4';
import {rffFormSubmit} from '@mc/react-app-v4';
import {MapEditorActions, EditorTool} from '@mc/react-mapio';
import {PointCoordsModal} from '../forms/PointCoordsModal';
import {MultiCoordsModal} from '../forms/MultiCoordsModal';
import {CircleCoordsModal} from '../forms/CircleCoordsModal';
import {PolyFixedModal} from '../forms/PolyFixedModal';
import {MultipleFeaturesSelection} from '../modals/MultipleFeaturesSelection';

/**
 * Pokud je požadováno přidání do kolekce, propíše příznak `addToCollection` do parametrů nástroje.
 * Engine podle něj přidá nově zakreslenou geometrii do právě vybraného objektu (povýší jej na kolekci)
 * místo založení samostatného objektu. Funguje pro objektové i polní (souřadnice) parametry.
 */
const withAddToCollection = (data: any, extraParams?: {addToCollection?: boolean}): any => {
    if (!extraParams?.addToCollection) {
        return data;
    }
    if (data == null) {
        return {addToCollection: true};
    }
    // Pole (souřadnice) zkopírujeme a doplníme příznak jako vlastnost – nemutujeme vstup.
    if (Array.isArray(data)) {
        const copy: any = [...data];
        copy.addToCollection = true;
        return copy;
    }
    if (typeof data === 'object') {
        return {...data, addToCollection: true};
    }
    return data;
};

export const handleToolAction = async ({
    toolType,
    mapRef,
    selectedDetailFeature,
    extraParams,
    handleDefault,
}: {
    toolType: EditorTool | any;
    mapRef: React.MutableRefObject<MapEditorActions>;
    selectedDetailFeature?: any[];
    extraParams?: {addToCollection?: boolean};
    handleDefault?: () => void;
}) => {
    switch (toolType) {
        case EditorTool.POINT_COORDS:
            Modal.show(({onClose}) => (
                <PointCoordsModal
                    onClose={onClose}
                    title={'Vytvoření bodu zadáním souřadnic'}
                    onSubmit={async data => {
                        return await rffFormSubmit(async () => {
                            mapRef.current.activateToolWithType(toolType, withAddToCollection([data.y, data.x], extraParams));
                            onClose();
                        });
                    }}
                />
            ));
            break;

        case EditorTool.LINE_COORDS:
            Modal.show(({onClose}) => (
                <MultiCoordsModal
                    title={'Vytvoření linie zadáním souřadnic'}
                    initialValues={{
                        coordinates: [
                            {x: undefined, y: undefined},
                            {x: undefined, y: undefined},
                        ],
                    }}
                    onClose={onClose}
                    onSubmit={async data => {
                        return await rffFormSubmit(async () => {
                            mapRef.current.activateToolWithType(toolType, withAddToCollection(data, extraParams));
                            onClose();
                        });
                    }}
                />
            ));
            break;
        case EditorTool.POLYGON_COORDS:
            Modal.show(({onClose}) => (
                <MultiCoordsModal
                    title={'Vytvoření polygonu zadáním souřadnic'}
                    initialValues={{
                        coordinates: [
                            {x: undefined, y: undefined},
                            {x: undefined, y: undefined},
                            {x: undefined, y: undefined},
                            {x: undefined, y: undefined},
                        ],
                    }}
                    onClose={onClose}
                    onSubmit={async data => {
                        return await rffFormSubmit(async () => {
                            mapRef.current.activateToolWithType(toolType, withAddToCollection(data, extraParams));
                            onClose();
                        });
                    }}
                />
            ));
            break;

        case EditorTool.FIXED_RECTANGLE:
        case EditorTool.MULTI_FIXED_RECTANGLE:
        case EditorTool.CUTOUT_FIXED_RECTANGLE:
            Modal.show(({onClose}) => (
                <PolyFixedModal
                    title={'Vytvoření obdélníku zadáním rozměrů'}
                    onClose={onClose}
                    onSubmit={async data => {
                        return await rffFormSubmit(async () => {
                            mapRef.current.activateToolWithType(toolType, withAddToCollection(data, extraParams));
                            onClose();
                        });
                    }}
                />
            ));
            break;

        case EditorTool.CIRCLE_COORDS:
            Modal.show(({onClose}) => (
                <CircleCoordsModal
                    needCoords={true}
                    onClose={onClose}
                    title={'Vytvoření kruhu zadáním souřadnic'}
                    onSubmit={async data => {
                        return await rffFormSubmit(async () => {
                            mapRef.current.activateToolWithType(
                                toolType,
                                withAddToCollection(
                                    {
                                        radius: data.radius,
                                        center: [data.y, data.x],
                                    },
                                    extraParams,
                                ),
                            );
                            onClose();
                        });
                    }}
                />
            ));
            break;

        case EditorTool.CIRCLE_RADIUS:
            Modal.show(({onClose}) => (
                <CircleCoordsModal
                    needCoords={false}
                    onClose={onClose}
                    title={'Vytvoření kruhu zadáním poloměru'}
                    onSubmit={async data => {
                        return await rffFormSubmit(async () => {
                            mapRef.current.activateToolWithType(toolType, withAddToCollection({radius: data.radius}, extraParams));
                            onClose();
                        });
                    }}
                />
            ));
            break;

        case EditorTool.MERGE_LINES:
            Modal.show(({onClose}) => (
                <MultipleFeaturesSelection
                    title={'Spojit do linie'}
                    onClose={onClose}
                    featurePointers={selectedDetailFeature}
                    onClick={feature => {
                        mapRef.current.activateToolWithType(toolType, {featurePointers: [feature]});
                    }}
                />
            ));
            break;

        case EditorTool.LINE_OFFSET:
            mapRef.current.activateToolWithType(toolType, withAddToCollection(null, extraParams));
            break;
        default:
            if (handleDefault) {
                handleDefault();
            }
            break;
    }
};
